import { useApolloClient } from '@apollo/react-hooks';
import { useCallback, useRef } from 'react';

import { getDwellTimeHelper } from '@confluence/analytics-web-client';

import { useSendAnalyticsEvents } from '../analytics/useSendAnalyticsEvents';
import type { DwellTimeHelperData, DwellTimeHelperType } from '../analytics/DwellTimeHelper';

import { loadSpaceAndContentData } from './getConsumptionEventData';
import {
	createConsumptionErrorEvent,
	createConsumptionCompletedEvent,
	createConsumptionInitiatedEvent,
	createConsumptionViewedEvent,
} from './ConsumptionEventMapping';
import type {
	ConsumptionContentAttributes,
	ConsumptionEventRequiredData,
	ConsumptionInitiatedEventData,
	ConsumptionViewedEvent,
} from './ConsumptionEvents';

export const useConsumptionEvents = () => {
	const client = useApolloClient();
	const { sendEvent } = useSendAnalyticsEvents();
	const dwellTimeHelperRef = useRef<DwellTimeHelperType | null>(null);

	const sendErrorEvent = useCallback(
		(errorMessage: string, data: ConsumptionEventRequiredData) => {
			const unifiedErrorEvent = createConsumptionErrorEvent(errorMessage, data);
			sendEvent(unifiedErrorEvent);
		},
		[sendEvent],
	);

	const sendInitiatedEvent = useCallback(
		(
			data: ConsumptionInitiatedEventData,
			contentAttributesOverride?: Partial<ConsumptionContentAttributes>,
		) => {
			loadSpaceAndContentData(client, data.spaceKey, data.contentId)
				.then((response) => {
					const event = createConsumptionInitiatedEvent(response, data, contentAttributesOverride);
					sendEvent(event);
				})
				.catch((error: Error) => sendErrorEvent(error.message, data));
		},
		[client, sendErrorEvent, sendEvent],
	);

	const sendViewedEvent = useCallback(
		(
			data: ConsumptionEventRequiredData,
			contentAttributesOverride?: Partial<ConsumptionContentAttributes>,
		) => {
			loadSpaceAndContentData(client, data.spaceKey, data.contentId)
				.then((response) => {
					const event = createConsumptionViewedEvent(response, data, contentAttributesOverride);
					sendEvent(event);
				})
				.catch((error: Error) => sendErrorEvent(error.message, data));
		},
		[client, sendErrorEvent, sendEvent],
	);

	const sendCompletedEvent = useCallback(
		(event: ConsumptionViewedEvent, dwellTimeHelperData: DwellTimeHelperData) => {
			if (dwellTimeHelperData.attributes.finalDwellEvent) {
				const consumptionCompletedEvent = createConsumptionCompletedEvent(
					event,
					dwellTimeHelperData,
					document.visibilityState,
				);
				sendEvent(consumptionCompletedEvent, true);
			}
		},
		[sendEvent],
	);

	const sendViewedEventAndCompletedEvent = useCallback(
		(
			data: ConsumptionEventRequiredData,
			contentAttributesOverride?: Partial<ConsumptionContentAttributes>,
		) => {
			const sendEventPromise = loadSpaceAndContentData(client, data.spaceKey, data.contentId).then(
				(response) => {
					const event = createConsumptionViewedEvent(response, data, contentAttributesOverride);
					sendEvent(event);
					return event;
				},
			);

			Promise.all([sendEventPromise, getDwellTimeHelper() as Promise<typeof DwellTimeHelperType>])
				.then(([event, DwellTimeHelper]) => {
					const dwellTimeHelper = new DwellTimeHelper(
						(callbackData) => sendCompletedEvent(event, callbackData),
						event.data,
					);
					dwellTimeHelper.start();

					dwellTimeHelperRef.current = dwellTimeHelper;
				})
				.catch((error: Error) => {
					sendErrorEvent(error.message, data);
				});
		},
		[client, sendEvent, sendCompletedEvent, sendErrorEvent],
	);

	const stopDwellTimeTracker = useCallback(() => {
		if (dwellTimeHelperRef.current) {
			dwellTimeHelperRef.current.stop();
		}
	}, []);

	return {
		sendInitiatedEvent,
		sendViewedEvent,
		sendViewedEventAndCompletedEvent,
		sendCompletedEvent,
		sendErrorEvent,
		stopDwellTimeTracker,
	};
};
